import React, { useCallback, useEffect, useId, useState } from 'react';
import { Laptop, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { Modal } from './Modal';
import { ConfirmDialog } from './ConfirmDialog';
import { User } from '../types';
import { useToast } from '../contexts/ToastContext';
import {
  checkThisDevice, confirmMyDevice, getMyDevices, MyDevice, reportMyDevice, revokeMyDevice, ThisDeviceResult,
} from '../services/deviceTrustService';
import { canRequestHere, suggestDeviceLabel } from '../services/deviceIdentity';
import { formatVerifyCode } from '../utils/deviceProof';

/**
 * 설정 > 내 기기 (기기 신뢰 — server/routes/devices.js /this · /mine)
 *
 * - 이 기기: 승인 상태. 관찰 단계에서 모바일은 여기서 승인을 요청한다(대기 중이면 확인 코드).
 * - 내 기기 목록: 분실한 폰 해제 · 다른 기기의 대기 요청을 **본인 확인**(그 기기에 뜬 코드 입력) ·
 *   내가 하지 않은 요청이면 "내 요청이 아닙니다"(그 키를 거절하고 관리자에게 알린다).
 * 본인 확인은 이미 승인된 기기에서만 된다(서버가 판정 — canConfirm).
 */

interface MyDevicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: User;
}

const STATUS_TEXT: Record<MyDevice['status'], string> = {
  approved: '승인됨',
  pending: '승인 대기',
  rejected: '거절됨',
  revoked: '해제됨',
  expired: '만료됨',
};

function deviceName(d: Pick<MyDevice, 'label' | 'computerName' | 'kind'>): string {
  return d.label || d.computerName || (d.kind === 'desktop' ? 'PC 앱' : '모바일 기기');
}

function relative(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 3_600_000) return `${Math.max(1, Math.floor(diff / 60_000))}분 전`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}시간 전`;
  return new Date(iso).toLocaleDateString('ko-KR');
}

export const MyDevicesModal: React.FC<MyDevicesModalProps> = ({ isOpen, onClose, currentUser }) => {
  const fid = useId();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [thisDevice, setThisDevice] = useState<ThisDeviceResult | null>(null);
  const [devices, setDevices] = useState<MyDevice[]>([]);
  const [canConfirm, setCanConfirm] = useState(false);
  const [label, setLabel] = useState(() => suggestDeviceLabel());
  const [busy, setBusy] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<MyDevice | null>(null);
  const [code, setCode] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<MyDevice | null>(null);
  const [reportTarget, setReportTarget] = useState<MyDevice | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mine, me] = await Promise.all([
        getMyDevices(),
        checkThisDevice(currentUser).catch(() => null),
      ]);
      setDevices(mine.devices);
      setCanConfirm(mine.canConfirm);
      setThisDevice(me);
    } catch {
      showToast({ type: 'error', message: '기기 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setLoading(false);
    }
  }, [currentUser, showToast]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const requestApproval = async () => {
    setBusy(true);
    try {
      const r = await checkThisDevice(currentUser, { requestApproval: true, label: label.trim() });
      setThisDevice(r);
      showToast({ type: 'success', message: '승인을 요청했습니다. 확인 코드로 본인 확인을 해 주세요.' });
      load();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : '승인을 요청하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setBusy(false);
    }
  };

  const submitConfirm = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      await confirmMyDevice(confirmTarget.id, code);
      showToast({ type: 'success', message: '본인 확인을 마쳤습니다. 관리자가 승인하면 그 기기에서 쓸 수 있습니다.' });
      setConfirmTarget(null);
      setCode('');
      load();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : '본인 확인을 하지 못했습니다.' });
    } finally {
      setBusy(false);
    }
  };

  const doRevoke = async () => {
    const target = revokeTarget;
    setRevokeTarget(null);
    if (!target) return;
    try {
      const r = await revokeMyDevice(target.id);
      // 지금 쓰는 기기면 서버가 이 세션을 끊는다(auth:revoked → 로그아웃)
      if (!r.wasCurrent) {
        showToast({ type: 'success', message: '기기를 해제했습니다.' });
        load();
      }
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : '기기를 해제하지 못했습니다.' });
    }
  };

  const doReport = async () => {
    const target = reportTarget;
    setReportTarget(null);
    if (!target) return;
    try {
      await reportMyDevice(target.id);
      showToast({
        type: 'error',
        message: '요청을 거절하고 관리자에게 알렸습니다. 비밀번호가 새어 나갔을 수 있으니 인트라넷 비밀번호를 바로 바꾸세요.',
        duration: 15000,
      });
      load();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : '신고하지 못했습니다.' });
    }
  };

  const here = canRequestHere();
  const me = thisDevice?.device;
  const policyOff = thisDevice?.mode === 'off' && !thisDevice?.unsupported;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="내 기기" size="md">
        {loading && !thisDevice ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-5 text-sm">
            {/* 이 기기 */}
            <section className="rounded-xl border border-slate-200 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-slate-500" aria-hidden="true" />
                <span className="font-medium text-slate-800">이 기기</span>
                {me && <span className="text-xs text-slate-500">지문 {me.fingerprint}</span>}
              </div>
              {policyOff ? (
                <p className="text-slate-600">기기 승인 정책이 꺼져 있습니다. 지금은 따로 할 일이 없습니다.</p>
              ) : thisDevice?.unsupported ? (
                <p className="text-slate-600">이 브라우저에서는 기기를 등록할 수 없습니다(개인정보 보호 모드 등).</p>
              ) : me?.status === 'approved' ? (
                <p className="text-slate-600">승인된 기기입니다.</p>
              ) : me?.status === 'pending' ? (
                <div className="space-y-2">
                  <p className="text-slate-600">관리자 승인을 기다리고 있습니다.</p>
                  {me.selfConfirmed ? (
                    <p className="text-emerald-700">본인 확인을 마쳤습니다.</p>
                  ) : me.code ? (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center">
                      <div className="text-xs text-slate-500">확인 코드</div>
                      <div className="text-2xl font-bold tracking-widest tabular-nums text-slate-900">{formatVerifyCode(me.code)}</div>
                      <div className="text-xs text-slate-500 mt-1">이미 쓰는 PC 앱의 내 기기에서 입력하거나 관리자에게 알려 주세요</div>
                    </div>
                  ) : null}
                </div>
              ) : here.ok && thisDevice?.canRequest !== false ? (
                <form
                  className="space-y-2"
                  onSubmit={(e) => { e.preventDefault(); if (label.trim()) requestApproval(); }}
                >
                  <p className="text-slate-600">
                    등록되지 않은 기기입니다. 웹 브라우저 접속이 막힌 뒤에도 이 기기에서 쓰려면 승인을 요청해 두세요.
                  </p>
                  <label htmlFor={`${fid}-label`} className="block text-xs font-medium text-slate-700">기기 이름</label>
                  <div className="flex gap-2">
                    <input
                      id={`${fid}-label`}
                      value={label}
                      maxLength={40}
                      onChange={(e) => setLabel(e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg"
                    />
                    <button
                      type="submit"
                      disabled={busy || !label.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50"
                    >
                      승인 요청
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-slate-600">
                  {'reason' in here && here.reason === 'ios-browser'
                    ? '아이폰·아이패드는 홈 화면에 추가한 INA-TALK 앱에서 승인을 요청해 주세요.'
                    : '이 기기는 승인 요청 대상이 아닙니다. PC에서는 PC 앱을 이용해 주세요.'}
                </p>
              )}
            </section>

            {/* 내 기기 목록 */}
            <section className="space-y-2">
              <div className="font-medium text-slate-800">내 기기 목록</div>
              {devices.length === 0 ? (
                <p className="text-slate-500">등록된 기기가 없습니다</p>
              ) : (
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
                  {devices.map((d) => {
                    const Icon = d.kind === 'desktop' ? Laptop : Smartphone;
                    const open = d.status === 'approved' || d.status === 'pending';
                    return (
                      <li key={d.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                        <Icon size={16} className="shrink-0 text-slate-500" aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-slate-900">{deviceName(d)}</span>
                            {d.current && <span className="text-xs px-1.5 py-0.5 rounded bg-brand-100 text-brand-700">지금 이 기기</span>}
                            <span className="text-xs text-slate-500">{STATUS_TEXT[d.status]}</span>
                            {d.status === 'pending' && d.selfConfirmed && <span className="text-xs text-emerald-700">본인 확인됨</span>}
                          </div>
                          <div className="text-xs text-slate-400">
                            지문 {d.fingerprint} · {d.status === 'approved' ? `최근 사용 ${relative(d.lastSeenAt)}` : `요청 ${relative(d.requestedAt)}`}
                            {d.decisionReason && !open ? ` · ${d.decisionReason}` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {d.status === 'pending' && !d.current && !d.selfConfirmed && canConfirm && (
                            <button
                              type="button"
                              onClick={() => { setConfirmTarget(d); setCode(''); }}
                              className="px-2.5 py-1 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md"
                            >
                              본인 확인
                            </button>
                          )}
                          {d.status === 'pending' && !d.current && (
                            <button
                              type="button"
                              onClick={() => setReportTarget(d)}
                              className="px-2.5 py-1 text-xs bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 rounded-md"
                            >
                              내 요청이 아닙니다
                            </button>
                          )}
                          {open && (d.status === 'approved' || d.current) && (
                            <button
                              type="button"
                              onClick={() => setRevokeTarget(d)}
                              className="px-2.5 py-1 text-xs text-slate-600 border border-slate-300 hover:bg-slate-50 rounded-md"
                            >
                              해제
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {!canConfirm && devices.some((d) => d.status === 'pending' && !d.current) && (
                <p className="text-xs text-slate-500">본인 확인은 이미 승인된 내 기기(PC 앱 등)에서만 할 수 있습니다.</p>
              )}
            </section>
          </div>
        )}
      </Modal>

      {/* 본인 확인 — 새 기기에 뜬 코드 입력 */}
      <Modal
        isOpen={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        title="본인 확인"
        size="sm"
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setConfirmTarget(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
            <button
              type="button"
              onClick={submitConfirm}
              disabled={busy || code.replace(/\D/g, '').length !== 6}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50"
            >
              확인
            </button>
          </div>
        )}
      >
        {confirmTarget && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              <b>{deviceName(confirmTarget)}</b>(지문 {confirmTarget.fingerprint}) 화면에 뜬 6자리 확인 코드를 입력하세요.
              그 기기를 지금 손에 들고 있지 않다면 입력하지 말고 "내 요청이 아닙니다"를 누르세요.
            </p>
            <input
              inputMode="numeric"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, '').slice(0, 7))}
              className="block w-full px-3 py-2.5 text-center text-xl tracking-widest tabular-nums border border-slate-300 rounded-lg"
              placeholder="000 000"
              aria-label="확인 코드"
            />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!revokeTarget}
        title="기기 해제"
        message={revokeTarget?.current
          ? '지금 쓰고 있는 이 기기를 해제할까요? 바로 로그아웃되고, 다시 쓰려면 승인을 새로 받아야 합니다.'
          : `'${revokeTarget ? deviceName(revokeTarget) : ''}'을(를) 해제할까요? 그 기기는 바로 로그아웃되고 알림도 멈춥니다.`}
        confirmLabel="해제"
        danger
        onConfirm={doRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
      <ConfirmDialog
        isOpen={!!reportTarget}
        title="내 요청이 아닙니다"
        message="이 승인 요청을 거절하고 관리자에게 알릴까요? 요청이 만들어졌다는 것은 누군가 내 비밀번호를 입력했다는 뜻입니다."
        confirmLabel="신고"
        danger
        onConfirm={doReport}
        onCancel={() => setReportTarget(null)}
      />
    </>
  );
};
